import type { ISpeedDataMarkResponse } from "@framework/types";

import type { ISignalDataResponse } from "../../../scheme/components/analytics/interfaces";
import { ANALYTICS_MEASURE_TYPE } from "../../../scheme/components/analytics/interfaces";
import { transformSingleSignalToChart } from "../../../scheme/components/analytics/mappers/signalMappers";
import {
  CASCADE_SORT_MODE,
  type IMultiAnalysisDataMark,
  type IMultiAnalysisTimeRange,
  type IStructureTreePoint,
  type TCascadeSortMode,
  type TSignalDisplayUnit,
} from "../../../multi-analysis/types";
import { convertSignalChartUnits } from "../../../multi-analysis/utils/signal-unit-conversion";

export const CASCADE_MEASURE_TYPE = 5;
export const CASCADE_ENVELOPE_RANGE_TYPE = 3;
export const CASCADE_ROTATION_FREQUENCY_UNIT_CODE = 140;
export const CASCADE_FREQUENCY_HZ_UNIT_CODE = 3;

export interface ICascadeControlParameter {
  title: string;
  units: string;
  value: number;
  isRotationFrequency: boolean;
}

export interface ICascadeDataMark extends IMultiAnalysisDataMark {
  cascade: { controlParameter: ICascadeControlParameter | null; rangeType: number | null };
}

export interface ICascadeSample {
  key: string;
  date: string;
  value: number;
  rotationFrequencyRpm: number | null;
}

export interface ICascadeTrendSeries {
  key: string;
  pointId: number;
  measureId: number;
  name: string;
  pointName: string;
  measureName: string;
  unitCode: number;
  units: string;
  color: string;
  samples: readonly ICascadeSample[];
}

export interface ICascadeSpectrum {
  key: string;
  seriesKey: string;
  pointName: string;
  measureName: string;
  date: string;
  rotationFrequencyRpm: number | null;
  color: string;
  unitCode: number;
  units: string;
  frequencyUnitCode: number;
  frequencyUnits: string;
  items: readonly { x: number; y: number }[];
  value1: number;
}

export const parseCascadeWallClock = (value: string): number | null => {
  const match = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2})(?:\.(\d{1,3}))?)?/.exec(value);
  if (!match) return null;
  const [, year, month, day, hour, minute, second = "0", fraction = "0"] = match;
  const timestamp = Date.UTC(
    Number(year),
    Number(month) - 1,
    Number(day),
    Number(hour),
    Number(minute),
    Number(second),
    Number(fraction.padEnd(3, "0")),
  );
  const parsed = new Date(timestamp);
  return parsed.getUTCFullYear() === Number(year) &&
    parsed.getUTCMonth() === Number(month) - 1 &&
    parsed.getUTCDate() === Number(day) &&
    parsed.getUTCHours() === Number(hour) &&
    parsed.getUTCMinutes() === Number(minute) &&
    parsed.getUTCSeconds() === Number(second)
    ? timestamp
    : null;
};

export const isCascadeDataMark = (mark: IMultiAnalysisDataMark): mark is ICascadeDataMark => "cascade" in mark;

export const mapCascadeDataMarks = (
  rows: readonly ISpeedDataMarkResponse[],
  points: readonly IStructureTreePoint[],
  formatDirection: (direction: number) => string,
  controlParameter: { dataId: number; title: string; units: string } | null,
): ICascadeDataMark[] => {
  const pointsById = new Map(points.map(point => [point.id, point]));
  const marks = new Map<string, ICascadeDataMark>();

  for (const row of rows) {
    const point = pointsById.get(row.idPoint);
    if (
      !point ||
      row.Type !== CASCADE_MEASURE_TYPE ||
      row.RangeType === CASCADE_ENVELOPE_RANGE_TYPE ||
      !Number.isFinite(row.Value1) ||
      parseCascadeWallClock(row.Date) === null
    ) {
      continue;
    }

    const key = `${row.idPoint}:${row.idMeasure}:${row.Date}`;
    const direction = Number.isFinite(point.direction) ? formatDirection(point.direction).trim() : "";
    const directionLabel = direction.startsWith("(") && direction.endsWith(")") ? direction : `(${direction})`;
    marks.set(key, {
      key,
      idMeasure: row.idMeasure,
      idPoint: row.idPoint,
      date: row.Date,
      measureDate: row.Date,
      pointName: direction ? `${point.title} ${directionLabel}` : point.title,
      measureName: row.Name,
      value: row.Value1,
      value2: row.Value2 !== null && Number.isFinite(row.Value2) ? row.Value2 : null,
      units: row.Units,
      alarmStatus: row.AlarmStatus,
      rotationFrequencyRpm: row.RPM !== null && Number.isFinite(row.RPM) ? row.RPM : null,
      param1: row.Param1,
      param2: row.Param2,
      cascade: {
        rangeType: row.RangeType ?? null,
        controlParameter:
          controlParameter && row.ControlValue !== null && Number.isFinite(row.ControlValue)
            ? {
                title: controlParameter.title,
                units: controlParameter.units,
                value: row.ControlValue,
                isRotationFrequency: row.Key01 !== null && controlParameter.dataId === row.Key01,
              }
            : null,
      },
    });
  }

  return [...marks.values()].sort(
    (left, right) =>
      parseCascadeWallClock(left.date)! - parseCascadeWallClock(right.date)! || left.key.localeCompare(right.key),
  );
};

export const getCascadeFrameMarks = (
  marks: readonly IMultiAnalysisDataMark[],
  frameRange: IMultiAnalysisTimeRange,
): ICascadeDataMark[] => {
  const parseBound = (value: IMultiAnalysisTimeRange["start"]) =>
    value ? parseCascadeWallClock(value instanceof Date ? value.toISOString() : value) : null;
  const start = parseBound(frameRange.start);
  const end = parseBound(frameRange.end);
  if (start === null || end === null) return [];
  const lower = Math.min(start, end);
  const upper = Math.max(start, end);
  return marks.filter(isCascadeDataMark).filter(mark => {
    const timestamp = parseCascadeWallClock(mark.date);
    return timestamp !== null && timestamp >= lower && timestamp <= upper;
  });
};

export const buildCascadeTrendSeries = (
  marks: readonly ICascadeDataMark[],
  getUnits: (unitCode: number) => string,
  getColor: (key: string) => string,
): ICascadeTrendSeries[] => {
  const grouped = new Map<string, Omit<ICascadeTrendSeries, "samples"> & { samples: ICascadeSample[] }>();
  for (const mark of marks) {
    const key = `${mark.idPoint}:${mark.idMeasure}:${mark.units}`;
    const series = grouped.get(key) ?? {
      key,
      pointId: mark.idPoint,
      measureId: mark.idMeasure,
      name: `${mark.pointName} / ${mark.measureName}`,
      pointName: mark.pointName,
      measureName: mark.measureName,
      unitCode: mark.units,
      units: getUnits(mark.units),
      color: getColor(key),
      samples: [],
    };
    series.samples.push({
      key: mark.key,
      date: mark.date,
      value: mark.value,
      rotationFrequencyRpm: mark.rotationFrequencyRpm,
    });
    grouped.set(key, series);
  }

  return [...grouped.values()].map(series => ({
    ...series,
    samples: series.samples
      .slice()
      .sort((left, right) => parseCascadeWallClock(left.date)! - parseCascadeWallClock(right.date)!),
  }));
};

export const getCascadeThirdAxisValue = (
  mark: { date: string; rotationFrequencyRpm: number | null },
  mode: TCascadeSortMode,
): number | null => {
  if (mode === CASCADE_SORT_MODE.RPM) {
    return mark.rotationFrequencyRpm !== null && Number.isFinite(mark.rotationFrequencyRpm)
      ? mark.rotationFrequencyRpm
      : null;
  }
  return parseCascadeWallClock(mark.date);
};

export const compareCascadeThirdAxis = (
  left: { key: string; date: string; rotationFrequencyRpm: number | null },
  right: { key: string; date: string; rotationFrequencyRpm: number | null },
  mode: TCascadeSortMode,
): number => {
  const leftValue = getCascadeThirdAxisValue(left, mode);
  const rightValue = getCascadeThirdAxisValue(right, mode);
  if (leftValue === null && rightValue === null) {
    return parseCascadeWallClock(left.date)! - parseCascadeWallClock(right.date)! || left.key.localeCompare(right.key);
  }
  if (leftValue === null) return 1;
  if (rightValue === null) return -1;
  return (
    leftValue - rightValue ||
    parseCascadeWallClock(left.date)! - parseCascadeWallClock(right.date)! ||
    left.key.localeCompare(right.key)
  );
};

const CASCADE_CLOCK_MATCH_TOLERANCE_MS = 1000;

const signalClock = (signal: ISignalDataResponse): number | null => parseCascadeWallClock(signal.date);

const nearestSignalIndex = (
  signals: readonly ISignalDataResponse[],
  used: ReadonlySet<number>,
  idMeasure: number,
  target: number,
): number => {
  let bestIndex = -1;
  let bestDiff = Number.POSITIVE_INFINITY;
  signals.forEach((signal, index) => {
    if (used.has(index) || signal.idMeasure !== idMeasure) return;
    const time = signalClock(signal);
    if (time === null) return;
    const diff = Math.abs(time - target);
    if (diff < bestDiff) {
      bestDiff = diff;
      bestIndex = index;
    }
  });
  return bestIndex;
};

const clockOffsetForMeasure = (
  marks: readonly ICascadeDataMark[],
  signals: readonly ISignalDataResponse[],
  idMeasure: number,
): number => {
  const markTimes = marks
    .filter(mark => mark.idMeasure === idMeasure)
    .map(mark => parseCascadeWallClock(mark.date))
    .filter((time): time is number => time !== null);
  const signalTimes = signals
    .map(signal => (signal.idMeasure === idMeasure ? signalClock(signal) : null))
    .filter((time): time is number => time !== null);
  if (markTimes.length < 2 || signalTimes.length < 2) return 0;

  let bestOffset = 0;
  let bestScore = 0;
  for (const signalTime of signalTimes) {
    const offset = signalTime - markTimes[0];
    const used = new Set<number>();
    let score = 0;
    for (const markTime of markTimes) {
      let candidate = -1;
      let candidateDiff = Number.POSITIVE_INFINITY;
      signalTimes.forEach((time, index) => {
        if (used.has(index)) return;
        const diff = Math.abs(time - (markTime + offset));
        if (diff < candidateDiff) {
          candidateDiff = diff;
          candidate = index;
        }
      });
      if (candidate >= 0 && candidateDiff <= CASCADE_CLOCK_MATCH_TOLERANCE_MS) {
        used.add(candidate);
        score += 1;
      }
    }
    if (score > bestScore) {
      bestScore = score;
      bestOffset = offset;
    }
  }

  return bestScore >= 2 ? bestOffset : 0;
};

const matchSignalsToMarks = (
  marks: readonly ICascadeDataMark[],
  signals: readonly ISignalDataResponse[],
): Array<ISignalDataResponse | null> => {
  const used = new Set<number>();
  const offsets = new Map<number, number>();
  const offsetFor = (idMeasure: number) => {
    const cached = offsets.get(idMeasure);
    if (cached !== undefined) return cached;
    const offset = clockOffsetForMeasure(marks, signals, idMeasure);
    offsets.set(idMeasure, offset);
    return offset;
  };

  return marks.map(mark => {
    const exact = signals.findIndex(
      (signal, index) => !used.has(index) && signal.idMeasure === mark.idMeasure && signal.date === mark.date,
    );
    if (exact >= 0) {
      used.add(exact);
      return signals[exact];
    }

    const target = parseCascadeWallClock(mark.date);
    if (target === null) return null;
    const bestIndex = nearestSignalIndex(signals, used, mark.idMeasure, target + offsetFor(mark.idMeasure));
    if (bestIndex >= 0) {
      used.add(bestIndex);
      return signals[bestIndex];
    }

    return null;
  });
};

export const buildCascadeSpectra = (
  marks: readonly ICascadeDataMark[],
  signals: readonly ISignalDataResponse[],
  series: readonly ICascadeTrendSeries[],
  getFrequencyUnits: (unitCode: number) => string,
  targetUnits?: TSignalDisplayUnit,
): ICascadeSpectrum[] => {
  const seriesByKey = new Map(series.map(item => [item.key, item]));
  const matchedSignals = matchSignalsToMarks(marks, signals);
  return marks.flatMap((mark, index) => {
    const seriesItem = seriesByKey.get(`${mark.idPoint}:${mark.idMeasure}:${mark.units}`);
    const signal = matchedSignals[index];
    if (!seriesItem || !signal) return [];
    const chart = transformSingleSignalToChart(signal, seriesItem.pointName, ANALYTICS_MEASURE_TYPE.SPECTRUM);
    const sourceChart = {
      ...chart,
      rotationFrequencyRpm: chart.rotationFrequencyRpm ?? mark.rotationFrequencyRpm,
    };
    const converted =
      targetUnits === undefined ? sourceChart : convertSignalChartUnits(sourceChart, targetUnits, getFrequencyUnits);
    const frequencyUnitCode = converted.unitsXCode ?? CASCADE_FREQUENCY_HZ_UNIT_CODE;
    const amplitudeConverted = converted.unitsYCode != null && converted.unitsYCode !== seriesItem.unitCode;
    const amplitudeUnitCode = amplitudeConverted ? Number(converted.unitsYCode) : seriesItem.unitCode;
    return [
      {
        key: mark.key,
        seriesKey: seriesItem.key,
        pointName: seriesItem.pointName,
        measureName: seriesItem.measureName,
        date: mark.date,
        rotationFrequencyRpm: mark.rotationFrequencyRpm,
        color: seriesItem.color,
        unitCode: amplitudeUnitCode,
        units: amplitudeConverted ? getFrequencyUnits(amplitudeUnitCode) : seriesItem.units,
        frequencyUnitCode,
        frequencyUnits: getFrequencyUnits(frequencyUnitCode),
        items: converted.items.map(item => ({
          x: item.x,
          y: typeof item.y === "number" ? item.y : item.y.real ?? 0,
        })),
        value1: mark.value,
      },
    ];
  });
};

export const CASCADE_SPECTRUM_CHUNK_SIZE = 8;

export const chunkCascadeItems = <T>(items: readonly T[], size = CASCADE_SPECTRUM_CHUNK_SIZE): T[][] => {
  if (!items.length) return [];
  const chunkSize = Math.max(1, size);
  const chunks: T[][] = [];
  for (let index = 0; index < items.length; index += chunkSize) chunks.push(items.slice(index, index + chunkSize));
  return chunks;
};

export const sortCascadeSpectra = (spectra: readonly ICascadeSpectrum[], mode: TCascadeSortMode): ICascadeSpectrum[] =>
  spectra.slice().sort((left, right) => compareCascadeThirdAxis(left, right, mode));

export const resolveCascadeCursor = (
  marks: readonly ICascadeDataMark[],
  cursorKey: string | null,
): ICascadeDataMark | null => marks.find(mark => mark.key === cursorKey) ?? marks[marks.length - 1] ?? null;

const cascadeSeriesKey = (mark: ICascadeDataMark): string => `${mark.idPoint}:${mark.idMeasure}:${mark.units}`;

export const resolveCascadeSeriesCursor = (
  marks: readonly ICascadeDataMark[],
  seriesKey: string,
  cursor: ICascadeDataMark | null,
  mode: TCascadeSortMode,
): ICascadeDataMark | null => {
  const seriesMarks = marks.filter(mark => cascadeSeriesKey(mark) === seriesKey);
  if (!seriesMarks.length) return null;
  if (cursor && cascadeSeriesKey(cursor) === seriesKey)
    return seriesMarks.find(mark => mark.key === cursor.key) ?? cursor;
  const anchor = cursor ? getCascadeThirdAxisValue(cursor, mode) : null;
  if (anchor === null) return seriesMarks[seriesMarks.length - 1] ?? null;
  return seriesMarks.reduce((best, mark) => {
    const value = getCascadeThirdAxisValue(mark, mode);
    const bestValue = getCascadeThirdAxisValue(best, mode);
    const distance = value === null ? Number.POSITIVE_INFINITY : Math.abs(value - anchor);
    const bestDistance = bestValue === null ? Number.POSITIVE_INFINITY : Math.abs(bestValue - anchor);
    return distance < bestDistance ? mark : best;
  });
};

export const getCascadeCursorOrder = (
  series: readonly ICascadeTrendSeries[],
  cursorKey: string | null,
  mode: TCascadeSortMode,
): ICascadeSample[] => {
  const activeSeries = series.find(item => item.samples.some(sample => sample.key === cursorKey)) ?? series[0];
  return (activeSeries?.samples ?? []).slice().sort((left, right) => compareCascadeThirdAxis(left, right, mode));
};

export const formatCascadeUnitLabel = (label: string): string => label.slice(label.lastIndexOf(",") + 1).trim();

export const formatCascadeDate = (date: string): string => {
  const match = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}:\d{2}:\d{2})/u.exec(date);
  return match ? `${match[3]}.${match[2]}.${match[1]} ${match[4]}` : date;
};

const sameCascadeUnit = (left: string, right: string): boolean =>
  formatCascadeUnitLabel(left).toLowerCase() === formatCascadeUnitLabel(right).toLowerCase();

const sameCascadeNumber = (left: number, right: number): boolean =>
  Math.abs(left - right) <= Math.max(Math.abs(left), Math.abs(right), 1) * 1e-6;

export const formatCascadeHeader = (
  mark: ICascadeDataMark,
  valueUnits: string,
  rotationFrequencyUnits: string,
  formatValue: (value: number, unitCode: number | null) => string,
  cursorValue: number | null = mark.value,
  amplitudeUnitCode: number | null = mark.units,
): string => {
  const control = mark.cascade.controlParameter;
  const parts = [mark.pointName, mark.measureName, formatCascadeDate(mark.date)];
  const controlValueLabel = control ? `${formatValue(control.value, null)} ${control.units}`.trim() : null;
  const rotationLabel =
    mark.rotationFrequencyRpm !== null
      ? `${formatValue(
          mark.rotationFrequencyRpm,
          CASCADE_ROTATION_FREQUENCY_UNIT_CODE,
        )} ${rotationFrequencyUnits}`.trim()
      : null;
  const controlAlreadyShowsRotation =
    control !== null &&
    controlValueLabel !== null &&
    rotationLabel !== null &&
    (control.isRotationFrequency ||
      controlValueLabel === rotationLabel ||
      (sameCascadeUnit(control.units, rotationFrequencyUnits) &&
        sameCascadeNumber(control.value, mark.rotationFrequencyRpm!)));
  if (control && controlValueLabel) parts.push(`${control.title}: ${controlValueLabel}`);
  if (rotationLabel && !controlAlreadyShowsRotation) parts.push(rotationLabel);
  const value = cursorValue !== null && Number.isFinite(cursorValue) ? cursorValue : mark.value;
  return `${parts.join(" / ")} : ${formatValue(value, amplitudeUnitCode)} ${valueUnits}`.trim();
};

export const cascadeThirdAxisLabelIndexes = (count: number, limit = 8): number[] => {
  if (count <= 0) return [];
  if (count <= limit) return Array.from({ length: count }, (_, index) => index);
  const last = count - 1;
  const indexes = new Set<number>();
  for (let index = 0; index < limit; index += 1) indexes.add(Math.round((index * last) / (limit - 1)));
  return [...indexes].sort((left, right) => left - right);
};
